import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Modal,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  InputAdornment,
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import { useLanguage } from "../../context/LanguageContext";
import generalApi from "../../api/generalApi";
import { toast } from "react-toastify";
import { languages } from "../../const/languages";
import ReactCountryFlag from "react-country-flag";
import AddProjectModal from "./AddProjectModal.jsx"; // Explicitly use .jsx extension

const MobileAddToProjectModal = ({ 
  open, 
  onClose, 
  contentType: initialContentType, 
  contentValue, 
  botTaskName
}) => {
  const { language } = useLanguage();
  const [name, setName] = useState("");
  const [currentContentType, setCurrentContentType] = useState(initialContentType || "Content");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get language name from language code
  const getLanguageName = (langCode) => {
    if (!langCode) return "Unknown";
    const lang = languages.find(l => l.code === langCode);
    return lang ? lang.name : "Unknown";
  };
  
  // Force visibility if open is true
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      // Load projects when modal opens
      fetchProjects();
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Reset form when modal is opened
  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
      setSelectedProject(null);
      setSearchTerm("");
      setCurrentContentType(initialContentType || "Content");
    }
  }, [open, initialContentType]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      setError(null);
      const response = await generalApi.project.getProjects();
      setProjects(response.results || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  // Handle project creation success
  const handleProjectAdded = async (newProject) => {
    try {
      setIsAddProjectModalOpen(false);
      const response = await generalApi.project.getProjects();
      setProjects(response.results || []);
      
      const latestProject = response.results?.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      )[0];
      
      if (latestProject) {
        setSelectedProject(latestProject);
      }
      
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Filter projects based on search term and language
  const filteredProjects = projects.filter(project => 
    project.language === language &&
    (!searchTerm || project.name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle save to project
  const handleSaveToProject = async () => {
    if (!selectedProject) {
      setError("Please select a project first");
      return;
    }

    if (!name.trim()) {
      setError("Please enter a name for this item");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let finalContentValue = contentValue;
      
      // If this is an image, upload it first
      if (initialContentType === "Image") {
        try {
          // Convert base64 to file
          const response = await fetch(contentValue);
          const blob = await response.blob();
          const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });

          // Create form data
          const formData = new FormData();
          formData.append('file', file);
          formData.append('projectId', selectedProject._id);

          // Upload to images server
          const uploadResponse = await generalApi.images.uploadImage(formData);
          
          if (!uploadResponse?.results?.url) {
            throw new Error('Failed to get upload URL');
          }

          finalContentValue = uploadResponse.results.url;
        } catch (error) {
          console.error('Error uploading image:', error);
          setError(error.response?.data?.error || 'Failed to upload image');
          setLoading(false);
          return;
        }
      }

      const projectItem = {
        name: name.trim(),
        type: initialContentType || "Content",
        content: finalContentValue,
      };

      await generalApi.project.addProjectItem(selectedProject._id, projectItem);
      toast.success('Saved to project successfully');
      onClose();
    } catch (err) {
      if (err.response) {
        if (err.response.status === 402) {
          setError("Subscription required to add more items.");
          onClose(); // Only close on 402
        } else if (err.response.status === 404) {
          setError("Project not found. The project may have been deleted.");
        } else if (err.response.status === 400) {
          setError(err.response.data?.message || "Invalid input data. Please check your entry.");
        } else {
          setError(`Server error (${err.response.status}): ${err.response.data?.message || "Please try again."}`);
        }
      } else if (err.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Failed to save to project. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={!loading ? onClose : undefined}
        aria-labelledby="mobile-add-to-project-modal"
        aria-describedby="add-content-to-project-on-mobile"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{
          width: '100%',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
          mx: 2,
          position: 'relative',
          top: 0,
          transform: 'none',
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            p: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <Typography variant="h6">Add to Project</Typography>
            <IconButton onClick={onClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setIsAddProjectModalOpen(true)}
              variant="outlined"
              size="medium"
              sx={{ 
                mb: 2,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                borderColor: '#be58da',
                color: '#be58da',
                '&:hover': {
                  borderColor: '#a347bf',
                  color: '#a347bf',
                  bgcolor: 'rgba(190, 88, 218, 0.04)'
                }
              }}
              fullWidth
            >
              New Project
            </Button>

            <TextField
              fullWidth
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'auto',
              mb: 2,
              maxHeight: '35vh'
            }}>
              {loadingProjects ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : filteredProjects.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No projects found
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredProjects.map((project) => (
                    <React.Fragment key={project._id}>
                      <ListItem disablePadding>
                        <ListItemButton 
                          id={`project-${project._id}`}
                          selected={selectedProject?._id === project._id}
                          onClick={() => setSelectedProject(project)}
                          sx={{ py: 1.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <FolderIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={project.name}
                          />
                        </ListItemButton>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Item Name"
                placeholder="Enter a name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
                error={!name.trim() && !!error}
                helperText={!name.trim() && error ? "Name is required" : ""}
                disabled={loading}
                variant="outlined"
              />
              <TextField
                label="Content Type"
                value={currentContentType}
                onChange={(e) => setCurrentContentType(e.target.value.slice(0, 12))}
                variant="outlined"
                sx={{ width: '40%' }}
                inputProps={{ maxLength: 12 }}
                disabled={initialContentType === "Image"}
              />
            </Box>

            {error && !error.includes("name") && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ 
            p: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          }}>
            <Button 
              onClick={handleSaveToProject}
              variant="contained"
              disabled={loading || !selectedProject || !name.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon sx={{ fontSize: '1.1rem' }} />}
              fullWidth
              size="large"
              sx={{ 
                bgcolor: "#9c27b0", 
                "&:hover": { bgcolor: "#7b1fa2" },
                py: 1.5,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {loading ? "Saving..." : "Save to Project"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <AddProjectModal
        open={isAddProjectModalOpen}
        onClose={() => setIsAddProjectModalOpen(false)}
        onProjectAdded={handleProjectAdded}
        defaultLanguage={language}
      />
    </>
  );
};

export default MobileAddToProjectModal; 